/**
 * The one-tap prediction made before a contrast twin (pedagogy spec §5.3.6, §13 Q14: required).
 * The authored prompt is the group's question; the choices are the three direction verbs, or the
 * authored options of an option-form prediction. The group is a fieldset of radios so a keyboard
 * and a screen reader get it for free; Enter and S on a focused radio start the contrast, and a
 * refused start (no choice) passes `error`, which is announced and moves focus here.
 *
 * `PredictionChoice` words a stored choice (a verb or an option id) for the recap on the twin and
 * the verdict in its feedback.
 */
import { forwardRef, useImperativeHandle, useRef } from "react";
import type { Localized as LocalizedValue } from "@shared/types";
import { Localized } from "@client/components/Localized";
import { RadioGroup } from "@client/components/ui";
import { useT } from "@client/i18n";
import { answerKeyDown } from "./answerKeys";
import { PREDICTION_VERBS } from "./followUp";
import type { PredictionVerb } from "./types";

export interface PredictionOption {
  id: string;
  text: LocalizedValue;
}

export interface PredictionCardHandle {
  /** Moves focus into the group: the checked choice, else the first. */
  focus(): void;
}

export interface PredictionCardProps {
  /** The authored question: "Hur ändras våglängden när …?". */
  prompt: LocalizedValue;
  /** An option-form prediction's choices; absent for the verb form. */
  options?: readonly PredictionOption[] | undefined;
  /** The chosen verb or option id; `null` until the student taps. */
  value: string | null;
  onChange: (choice: string) => void;
  /** Enter or S on a focused choice: start the contrast (refused without a choice). */
  onSubmit: () => void;
  /** Inline error from a refused start. */
  error?: string | undefined;
}

export const PredictionCard = forwardRef<PredictionCardHandle, PredictionCardProps>(
  function PredictionCard({ prompt, options, value, onChange, onSubmit, error }, ref) {
    const t = useT();
    const containerRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(
      ref,
      () => ({
        focus() {
          const radios =
            containerRef.current?.querySelectorAll<HTMLInputElement>('input[type="radio"]');
          if (!radios || radios.length === 0) return;
          (Array.from(radios).find((radio) => radio.checked) ?? radios[0]).focus();
        },
      }),
      [],
    );

    const choices = options
      ? options.map((option) => ({
          value: option.id,
          label: <Localized value={option.text} variant="inline" />,
        }))
      : PREDICTION_VERBS.map((verb) => ({ value: verb as string, label: t(verbKey(verb)) }));

    return (
      // Key events bubble from the radios; `RadioGroup` takes no handler of its own.
      <div ref={containerRef} onKeyDown={answerKeyDown({ submit: onSubmit })}>
        <RadioGroup
          legend={
            <>
              <span className="block text-xs font-medium uppercase tracking-wide text-ink-500 dark:text-ink-400">
                {t("practice.prediction.label")}
              </span>
              <Localized value={prompt} variant="inline" />
            </>
          }
          value={value}
          onChange={onChange}
          options={choices}
          className="!flex-row flex-wrap gap-x-5 gap-y-2 max-sm:gap-x-2"
          // Pill-style choices on phones: radio and label are one >= 44 px tap target, like the
          // confidence rating.
          optionClassName="max-sm:min-h-11 max-sm:items-center max-sm:gap-0 max-sm:rounded-lg max-sm:border max-sm:border-ink-300 max-sm:[&>input]:ml-3 dark:max-sm:border-ink-700"
          labelClassName="max-sm:flex max-sm:min-h-11 max-sm:items-center max-sm:pl-2 max-sm:pr-3"
        />
        {error && (
          <p role="alert" className="mt-1 text-xs text-red-700 dark:text-red-300">
            {error}
          </p>
        )}
      </div>
    );
  },
);

/** The i18n key of a direction verb. */
function verbKey(verb: PredictionVerb) {
  return `practice.prediction.${verb}` as const;
}

function isVerb(choice: string): choice is PredictionVerb {
  return (PREDICTION_VERBS as readonly string[]).includes(choice);
}

export interface PredictionChoiceProps {
  /** A verb, or the id of one of `options`. */
  choice: string;
  options?: readonly PredictionOption[] | undefined;
}

/** A stored choice in words: the option's text, or the verb ("Ökar"); an unknown id as it is. */
export function PredictionChoice({ choice, options }: PredictionChoiceProps) {
  const t = useT();
  const option = options?.find((candidate) => candidate.id === choice);
  if (option) return <Localized value={option.text} variant="inline" />;
  if (isVerb(choice)) return <>{t(verbKey(choice))}</>;
  return <>{choice}</>;
}
